/**
 * Sample Picker
 * Modal shown before checkout letting the customer pick free samples
 * (added to the cart at 0e). Intercepts the cart's checkout button(s),
 * enforces a cap of 1 sample per paid unit in the cart, and adds the
 * chosen samples via the Ajax Cart API before letting checkout proceed.
 */

if (!customElements.get('sample-picker-modal')) {
	customElements.define(
		'sample-picker-modal',
		class SamplePickerModal extends ModalDialog {
			constructor() {
				super()

				this.dialog = this.querySelector('.sample-picker-modal__dialog')
				this.grid = this.querySelector('[data-sample-grid]')
				this.counterEl = this.querySelector('[data-sample-counter]')
				this.errorEl = this.querySelector('[data-sample-error]')
				this.skipButton = this.querySelector('[data-sample-skip]')
				this.continueButton = this.querySelector('[data-sample-continue]')
				this.counterTemplate =
					this.getAttribute('data-counter-template') ||
					'[count]/[max] muestras seleccionadas'

				this.cards = Array.from(this.querySelectorAll('[data-sample-card]'))
				this.max = 0
				this.triggerButton = null
				this.triggerForm = null

				this.grid.addEventListener('click', this.onGridClick.bind(this))
				this.grid.addEventListener('change', this.onCardChange.bind(this))
				this.skipButton.addEventListener('click', this.onSkip.bind(this))
				this.continueButton.addEventListener(
					'click',
					this.onContinue.bind(this)
				)
			}

			/**
			 * Entry point called by the checkout button interceptor instead of a
			 * plain show(). Recomputes the cap against the live cart, trims any
			 * sample quantities that no longer fit, syncs the cards, then opens.
			 */
			async open(triggerButton) {
				this.triggerButton = triggerButton
				this.triggerForm = triggerButton.form || triggerButton.closest('form')
				triggerButton.classList.add('loading')
				triggerButton.setAttribute('aria-disabled', 'true')

				try {
					await this.syncWithCart()
				} catch (error) {
					console.error('Sample picker: could not read cart state', error)
				} finally {
					triggerButton.classList.remove('loading')
					triggerButton.removeAttribute('aria-disabled')
				}

				this.hideError()
				this.show(triggerButton)
			}

			/** Fetches the live cart, computes the cap, and trims excess samples. */
			async syncWithCart() {
				const response = await fetch(`${window.routes.cart_url}.js`)
				const cart = await response.json()

				let paidUnits = 0
				const sampleLines = []

				cart.items.forEach((item) => {
					if (item.properties && item.properties._sample) {
						sampleLines.push(item)
					} else {
						paidUnits += item.quantity
					}
				})

				this.max = paidUnits

				let selected = sampleLines.reduce((sum, item) => sum + item.quantity, 0)
				let excess = selected - this.max

				if (excess > 0) {
					for (const item of sampleLines) {
						if (excess <= 0) break
						const reduceBy = Math.min(excess, item.quantity)
						const newQuantity = item.quantity - reduceBy
						await this.changeLineQuantity(item, newQuantity)
						item.quantity = newQuantity
						excess -= reduceBy
					}
				}

				const quantityByVariant = {}
				sampleLines.forEach((item) => {
					if (item.quantity > 0) {
						quantityByVariant[item.variant_id] =
							(quantityByVariant[item.variant_id] || 0) + item.quantity
					}
				})

				this.cards.forEach((card) => {
					const variantId = card.getAttribute('data-variant-id')
					const input = card.querySelector('[data-sample-input]')
					input.value = quantityByVariant[variantId] || 0
				})

				this.updateCardStates()
			}

			changeLineQuantity(item, quantity) {
				return fetch(window.routes.cart_change_url, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
					body: JSON.stringify({ id: item.key, quantity }),
				})
			}

			onGridClick(event) {
				const button = event.target.closest(
					'.sample-card__minus, .sample-card__plus'
				)
				if (!button || button.classList.contains('disabled')) return

				event.preventDefault()
				const card = button.closest('[data-sample-card]')
				const input = card.querySelector('[data-sample-input]')

				if (button.classList.contains('sample-card__plus')) {
					input.stepUp()
				} else {
					input.stepDown()
				}

				this.updateCardStates()
			}

			onCardChange(event) {
				if (!event.target.matches('[data-sample-input]')) return
				this.updateCardStates()
			}

			getTotalSelected() {
				return this.cards.reduce((sum, card) => {
					const input = card.querySelector('[data-sample-input]')
					return sum + (parseInt(input.value, 10) || 0)
				}, 0)
			}

			updateCardStates() {
				const total = this.getTotalSelected()

				this.cards.forEach((card) => {
					const input = card.querySelector('[data-sample-input]')
					const plusButton = card.querySelector('.sample-card__plus')
					const minusButton = card.querySelector('.sample-card__minus')
					const quantity = parseInt(input.value, 10) || 0
					const remainingForThisCard = this.max - total + quantity

					input.max = Math.max(remainingForThisCard, quantity)

					card.classList.toggle('is-selected', quantity > 0)
					card.classList.toggle(
						'is-disabled',
						quantity === 0 && total >= this.max
					)
					plusButton.classList.toggle(
						'disabled',
						remainingForThisCard <= 0
					)
					minusButton.classList.toggle('disabled', quantity <= 0)
				})

				this.counterEl.textContent = this.counterTemplate
					.replace('[count]', total)
					.replace('[max]', this.max)

				// Once the customer has picked at least one sample, "skip" no
				// longer makes sense - they must confirm via "continue" instead.
				this.skipButton.toggleAttribute('disabled', total > 0)
				this.skipButton.classList.toggle('is-disabled', total > 0)
			}

			hideError() {
				this.errorEl.hidden = true
			}

			showError(message) {
				if (message) this.errorEl.textContent = message
				this.errorEl.hidden = false
			}

			onSkip(event) {
				event.preventDefault()
				this.finishCheckout()
			}

			async onContinue(event) {
				event.preventDefault()
				this.hideError()

				const items = this.cards
					.map((card) => {
						const input = card.querySelector('[data-sample-input]')
						const quantity = parseInt(input.value, 10) || 0
						if (quantity === 0) return null
						return {
							id: card.getAttribute('data-variant-id'),
							quantity,
							properties: { _sample: 'true' },
						}
					})
					.filter(Boolean)

				if (items.length === 0) {
					this.finishCheckout()
					return
				}

				this.continueButton.setAttribute('aria-disabled', 'true')
				this.continueButton.classList.add('loading')

				try {
					const response = await fetch(window.routes.cart_add_url, {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
							Accept: 'application/json',
						},
						body: JSON.stringify({ items }),
					})
					const data = await response.json()

					if (!response.ok || data.status) {
						throw new Error(data.description || data.message || 'cart_add_error')
					}

					this.finishCheckout()
				} catch (error) {
					console.error('Sample picker: add to cart failed', error)
					this.showError()
				} finally {
					this.continueButton.removeAttribute('aria-disabled')
					this.continueButton.classList.remove('loading')
				}
			}

			finishCheckout() {
				const form = this.triggerForm
				const button = this.triggerButton
				this.hide()
				if (!form) return
				// Pass the original button as submitter so its name="checkout"
				// value is included - a plain form.submit()/requestSubmit() with
				// no submitter would silently drop it and Shopify would treat
				// this as a cart update instead of a checkout redirect.
				if (typeof form.requestSubmit === 'function') {
					form.requestSubmit(button)
				} else {
					if (button && button.name) {
						const hidden = document.createElement('input')
						hidden.type = 'hidden'
						hidden.name = button.name
						hidden.value = button.value || ''
						form.appendChild(hidden)
					}
					form.submit()
				}
			}
		}
	)
}

/**
 * Checkout button interceptor. Delegated on `document` (rather than bound
 * directly to the button) because the cart drawer re-renders its inner
 * HTML on every cart change (`cart.renderContents()`), which would replace
 * the checkout button and orphan a directly-attached listener. If the
 * sample picker modal isn't in the DOM (feature disabled, or no eligible
 * sample products configured), this does nothing and the checkout
 * button(s) behave exactly as before.
 */
document.addEventListener('click', (event) => {
	const button = event.target.closest(
		'#CartDrawer-Checkout, .cart__checkout-button'
	)
	if (!button) return

	const modal = document.querySelector('sample-picker-modal')
	if (!modal) return

	event.preventDefault()
	modal.open(button)
})
